import { useId, useState } from 'react'
import { Link } from 'react-router'
import { post, type NodeInfo } from '../../api'
import { Button } from '../../components/ui'
import { FactorCancelled } from '../SecondFactor'
import { routePath, type RelayRoute } from './api'
import { ServerImpact, WordField, actionError, useConfirmedAction } from './confirm'
import { plural } from './format'
import { useRelayLive } from './live'
import { nodeLegs } from './nodes'

/*
 * Revoke a node (POST /api/admin/nodes/{id}/revoke; decision 48, I4 of the R0
 * review): the node stops ingest, outputs and its relay legs at once, leaves the
 * peers list and can't connect again with its credential or relay key. One
 * component for the node card's Relay key tab and for Node settings / Manage, so
 * both paths behave the same.
 *
 * The node's name is the word, typed before the request goes (an older center
 * revokes on any POST, so the word is never left to a second round). A center
 * with the confirmations of decision 48 answers a revoke of a node with live
 * legs with 400 confirm_required + impact when the word does not match, and with
 * 403 totp_required for the code (the code window, SecondFactor); without live
 * legs it asks for nothing more. Its `impact` replaces the console's estimate.
 */

export function RevokeNode({ n, routes: given, onRevoked, testId = 'node-revoke' }: { n: NodeInfo; routes?: RelayRoute[]; onRevoked: () => Promise<unknown> | void; testId?: string }) {
  const live = useRelayLive()
  const act = useConfirmedAction()
  const titleId = useId()
  const [open, setOpen] = useState(false)
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const [done, setDone] = useState('')
  const routes = given ?? (live.available ? [...live.routes.values()].filter((r) => r.path.some((p) => p.id === n.id)) : [])
  const { legs, legsUp, known } = nodeLegs(n)
  const asked = act.asked
  const word = asked?.word ?? n.name
  const value = asked ? act.word : typed
  const ok = value.trim() === word

  if (n.status === 'revoked') {
    return (
      <p className="text-sm text-muted" data-testid={`${testId}-done`}>
        {n.name} is revoked.
      </p>
    )
  }

  const close = () => {
    setOpen(false)
    setTyped('')
    setErr('')
    act.reset()
  }

  const revoke = async () => {
    setBusy(true)
    setErr('')
    try {
      const r = await act.run((c) => post<NodeInfo>(`/api/admin/nodes/${encodeURIComponent(n.id)}/revoke`, { confirm: c.confirm ?? typed.trim(), ...(c.totp ? { totp: c.totp } : {}) }), typed.trim())
      if (!r.ok) return // the center showed what the revoke takes and asks for its word
      setDone(`${n.name} revoked.`)
      setOpen(false)
      await onRevoked()
    } catch (e) {
      if (e instanceof FactorCancelled) return
      setErr(actionError(e, `Revoke ${n.name}`))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex flex-col gap-2" data-testid={testId}>
      <h3 className="font-medium text-fail">Revoke</h3>
      <p className="text-xs text-muted">
        The node stops ingest, outputs and relay legs at once, leaves the peers list and can't connect again. There is no undo: a revoked server needs a new token and a clean install.
      </p>
      {open ? (
        <div role="group" aria-labelledby={titleId} data-testid={`${testId}-confirm`} data-word={word} className="flex flex-col gap-2.5 rounded-md border border-fail/50 bg-fail/[.07] p-3">
          <p id={titleId} className="font-medium text-fail">
            Revoke {n.name}?
          </p>
          {asked ? (
            <ServerImpact impact={asked.impact} node={n.name} verb="Revoking takes" testId={`${testId}-server-impact`} />
          ) : (
            <div className="text-[13px]" data-testid={`${testId}-impact`}>
              <p className="font-medium text-text">What it touches</p>
              <p className="text-text-soft">
                {plural(n.streams, 'live stream')} with OBS on it
                {known ? ` · ${legs ? `${legsUp} of ${plural(legs, 'relay leg')} up` : 'no relay legs'}` : ''} · {plural(routes.length, 'route')} through it.
              </p>
              {routes.length > 0 && (
                <p className="mt-1 flex flex-wrap gap-x-2 gap-y-1 font-mono text-xs">
                  {routes.slice(0, 8).map((r) => (
                    <Link key={r.route_id} to={routePath(r.route_id)} className="text-text-soft underline underline-offset-2">
                      {r.stream}
                    </Link>
                  ))}
                  {routes.length > 8 && <span className="text-muted">+{routes.length - 8} more</span>}
                </p>
              )}
            </div>
          )}
          <div className="text-[13px]">
            <p className="font-medium text-text">What platforms see</p>
            <p className="text-text-soft" data-testid={`${testId}-platforms`}>
              {n.streams > 0 || routes.length > 0 || legs > 0 || asked
                ? 'Streams with OBS on this node stop now; streams relayed through it lose their path (the controller moves them to another hub or marks them NO PATH) and their outputs hold the platforms for the stream’s grace time.'
                : 'Nothing: no stream runs on or through this node.'}
            </p>
            {(routes.length > 0 || legs > 0) && !asked && (
              <p className="mt-1 text-xs text-muted">With live legs the center also asks for your code.</p>
            )}
          </div>
          <WordField word={word} value={value} onChange={asked ? act.setWord : setTyped} testId={`${testId}-name`} label="Type the node's name" />
          <div className="flex flex-wrap gap-2">
            <Button variant="quiet" className="max-md:min-h-[44px]" onClick={close}>
              Cancel
            </Button>
            <Button variant="danger" className="max-md:min-h-[44px]" disabled={busy || !ok} data-testid={`${testId}-apply`} onClick={() => void revoke()}>
              {busy ? 'Revoking…' : `Revoke ${n.name}`}
            </Button>
          </div>
        </div>
      ) : (
        <div>
          <Button variant="danger" className="max-md:min-h-[44px]" data-testid={`${testId}-${n.id}`} onClick={() => (setOpen(true), setDone(''))}>
            Revoke…
          </Button>
        </div>
      )}
      {err && (
        <p role="alert" className="rounded-md border border-fail/50 bg-fail/[.07] px-3 py-2 text-sm text-fail" data-testid={`${testId}-error`}>
          {err}
        </p>
      )}
      {done && (
        <p role="status" className="text-sm text-text-soft">
          {done}
        </p>
      )}
    </div>
  )
}
